/**
 * roomMachine — la salle d'un live, vue de l'app : la connexion que le SDK rapporte, les coupures et
 * leurs reprises, l'autre onglet, et le relevé du serveur qui fait foi.
 *
 * La partie COMMUNE du live (`engine/CLAUDE.md`) : rien n'y est propre à l'élève, le tuteur la
 * reprendra. Elle ne fait rien elle-même — elle rend l'état et les signaux de chaque transition, le
 * crochet (`useLiveRoom`) joue les signaux : mesurer une coupure, relire le relevé tout de suite,
 * relire ce que la coupure a pu faire perdre.
 *
 * Les règles :
 *  - **la reprise reste au SDK** (`reconnectPolicy`, `roomOptions.js`) : la machine ne décide jamais
 *    de reconnecter, elle constate ;
 *  - **chaque coupure est mesurée** : elle commence quand une salle déjà connectée passe en reprise ou
 *    se coupe, elle finit à la connexion suivante (signal `outage_ended`, avec sa durée) ;
 *  - **le serveur fait foi** : le relevé (`GET api/v2/live-sessions/{id}`) se relit tout de suite à
 *    chaque reprise, puis toutes les `POLL_OUTAGE_MS` tant que la salle n'est pas connectée ou que le
 *    relevé échoue, toutes les `POLL_MS` sinon ;
 *  - **un trou réseau ou une erreur serveur au relevé ne fait jamais sortir** : seuls un 403 (pas le
 *    bon jour), un 402 (paiement) ou un 404 (séance disparue) le font. C'est le défaut de
 *    `LiveMainLayout.js`, qui déconnectait sur toute erreur, hors ligne compris ;
 *  - **la séance close se lit au relevé** (`today_occurrence.finalized`), et elle peut se rouvrir (le
 *    BO) : la machine garde la dernière lecture, jamais une valeur figée ;
 *  - **une reprise sans issue se constate** : chez le lecteur qui pose un plafond (`STALL_CHECK`), une
 *    coupure qui reste en reprise ou en connexion plus longtemps que lui, réseau présent, est déclarée
 *    bloquée (signal `stalled`, une fois par plafond écoulé). La machine ne relance toujours rien : le
 *    crochet le fait. Sans plafond, rien ne change.
 *
 * Pure : l'heure arrive dans l'événement.
 */

/** Ce que le SDK dit de la salle (`ConnectionState` de livekit-client, `signalReconnecting` compris). */
export type SdkConnectionState = "connecting" | "connected" | "reconnecting" | "signalReconnecting" | "disconnected"

/**
 * La connexion vue de l'app. `idle` : rien encore ; `evicted` : coupée parce qu'un autre onglet a pris
 * la salle (même compte, même identité) — elle ne reprend pas tant qu'il est là.
 */
export type RoomConnection = "idle" | "connecting" | "connected" | "reconnecting" | "disconnected" | "evicted"

/** Ce qui fait sortir du live, au relevé : pas le bon jour, le paiement, la séance disparue. */
export type RoomExit = "forbidden" | "payment" | "gone"

/** La séance du jour, telle que le relevé la sert (`today_occurrence`). */
export type RoomSnapshot = {
    occurrenceId: number | null
    finalized: boolean
    finalizedAt: string | null
}

export type RoomState = {
    connection: RoomConnection
    /** La salle a déjà été connectée une fois : une coupure ensuite est une coupure, pas l'entrée. */
    everConnected: boolean
    /** La coupure en cours : son genre et son début. */
    outage: { kind: "reconnecting" | "disconnected"; since: number } | null
    /** Le nombre de reprises : chaque reprise relit ce que la coupure a pu faire perdre. */
    recoveries: number
    /** La dernière lecture du relevé. `null` : pas encore lu. */
    snapshot: RoomSnapshot | null
    /** Les relevés ratés d'affilée (réseau, 5xx) : la fréquence monte, l'élève reste. */
    snapshotFailures: number
    exit: RoomExit | null
    /** Un autre onglet a la même séance ouverte. */
    otherTab: boolean
    /** L'appareil se dit en ligne. Hors ligne, une reprise qui dure attend le réseau : elle n'est pas bloquée. */
    online: boolean
    /**
     * Depuis quand le plafond court : une coupure en reprise ou en connexion, réseau présent. `null`
     * sinon (connectée, coupée, hors ligne). Une salle coupée puis relancée repart d'un plafond neuf.
     */
    stallClockSince: number | null
    /** La coupure en cours a dépassé le plafond au moins une fois : la salle peut être relancée. */
    stalled: boolean
}

export type RoomEvent =
    | { type: "SDK_STATE"; state: SdkConnectionState; at: number; duplicateIdentity?: boolean }
    | { type: "SNAPSHOT_READ"; snapshot: RoomSnapshot | null; at: number }
    | { type: "SNAPSHOT_FAILED"; status: number | null; offline: boolean; at: number }
    | { type: "OTHER_TAB"; present: boolean }
    | { type: "NETWORK"; online: boolean; at: number }
    /** Le plafond de qui le pose : la coupure a-t-elle passé `ceilingMs` sans issue ? */
    | { type: "STALL_CHECK"; at: number; ceilingMs: number }

export type RoomSignal =
    | { type: "transition"; from: RoomConnection; to: RoomConnection; event: string }
    | { type: "outage_started"; kind: "reconnecting" | "disconnected" }
    | { type: "outage_ended"; kind: "reconnecting" | "disconnected"; durationMs: number }
    /** Relire le relevé maintenant (une reprise, un premier connecté). */
    | { type: "read_snapshot" }
    /** Après une reprise : relire ce qu'une coupure a pu faire perdre (au lecteur de le dire). */
    | { type: "resync" }
    | { type: "exit"; exit: RoomExit; status: number }
    | { type: "snapshot_failed"; status: number | null; offline: boolean; failures: number }
    /**
     * La coupure a passé le plafond sans issue : à relancer. `first` : la première fois de cette
     * coupure (ce qui se rapporte une fois) ; `stuckMs` : depuis quand la salle n'avance plus.
     */
    | { type: "stalled"; first: boolean; connection: RoomConnection; stuckMs: number }

/** Le relevé, salle connectée et relevé sain. */
export const POLL_MS = 60 * 1000
/** Le relevé pendant une coupure ou après un relevé raté : plus souvent, pour que la fin rejoigne vite. */
export const POLL_OUTAGE_MS = 10 * 1000

export const initialRoomState: RoomState = {
    connection: "idle",
    everConnected: false,
    outage: null,
    recoveries: 0,
    snapshot: null,
    snapshotFailures: 0,
    exit: null,
    otherTab: false,
    online: true,
    stallClockSince: null,
    stalled: false,
}

/** L'heure où le plafond tombera, ou `null` : rien ne court. */
export function stallDeadline(state: RoomState, ceilingMs: number): number | null {
    return state.stallClockSince == null ? null : state.stallClockSince + ceilingMs
}

/** Le plafond court-il ? Une coupure en reprise ou en connexion, réseau présent ; il garde son départ. */
function withStallClock(state: RoomState, at: number): RoomState {
    const running =
        state.outage != null &&
        state.online &&
        (state.connection === "reconnecting" || state.connection === "connecting")
    const stallClockSince = running ? (state.stallClockSince ?? at) : null
    return stallClockSince === state.stallClockSince ? state : { ...state, stallClockSince }
}

/** Le code HTTP d'un relevé raté → ce qui fait sortir, ou `null` : on reste. */
export function exitForStatus(status: number | null): RoomExit | null {
    if (status === 403) return "forbidden"
    if (status === 402) return "payment"
    if (status === 404) return "gone"
    return null
}

/** Le délai jusqu'au prochain relevé. */
export function pollDelay(state: RoomState): number | null {
    if (state.exit != null) return null
    if (state.outage != null || state.snapshotFailures > 0) return POLL_OUTAGE_MS
    return POLL_MS
}

function connectionOf(state: SdkConnectionState, otherTab: boolean, duplicateIdentity: boolean): RoomConnection {
    switch (state) {
        case "connected":
            return "connected"
        case "connecting":
            return "connecting"
        case "reconnecting":
        case "signalReconnecting":
            return "reconnecting"
        case "disconnected":
            return otherTab || duplicateIdentity ? "evicted" : "disconnected"
    }
}

export function roomReducer(state: RoomState, event: RoomEvent): { state: RoomState; signals: RoomSignal[] } {
    switch (event.type) {
        case "SDK_STATE": {
            const to = connectionOf(event.state, state.otherTab, event.duplicateIdentity === true)
            if (to === state.connection) return { state, signals: [] }
            const signals: RoomSignal[] = [{ type: "transition", from: state.connection, to, event: event.state }]
            let next: RoomState = { ...state, connection: to }

            if (to === "connected") {
                if (state.outage != null) {
                    signals.push({
                        type: "outage_ended",
                        kind: state.outage.kind,
                        durationMs: Math.max(0, event.at - state.outage.since),
                    })
                    next = { ...next, outage: null, recoveries: state.recoveries + 1 }
                    signals.push({ type: "resync" })
                }
                if (!state.everConnected || state.outage != null) signals.push({ type: "read_snapshot" })
                next = { ...next, everConnected: true, stalled: false }
            } else if (state.everConnected && (to === "reconnecting" || to === "disconnected" || to === "evicted")) {
                // Une reprise qui piétine puis se coupe reste UNE coupure : son début ne bouge pas.
                const kind = to === "reconnecting" ? "reconnecting" : "disconnected"
                if (state.outage == null) {
                    next = { ...next, outage: { kind, since: event.at } }
                    signals.push({ type: "outage_started", kind })
                } else if (state.outage.kind !== kind && kind === "disconnected") {
                    next = { ...next, outage: { ...state.outage, kind } }
                }
            }
            // Un autre onglet a la salle : il n'y a rien à relancer ici.
            if (to === "evicted") next = { ...next, stalled: false }
            return { state: withStallClock(next, event.at), signals }
        }

        case "NETWORK": {
            if (event.online === state.online) return { state, signals: [] }
            // Hors ligne, le plafond s'arrête ; il repart du retour du réseau, entier.
            const next = withStallClock({ ...state, online: event.online, stallClockSince: null }, event.at)
            return { state: next, signals: [] }
        }

        case "STALL_CHECK": {
            const deadline = stallDeadline(state, event.ceilingMs)
            if (deadline == null || event.at < deadline) return { state, signals: [] }
            return {
                // Le plafond repart d'ici : une relance qui piétine à son tour sera relancée au suivant.
                state: { ...state, stalled: true, stallClockSince: event.at },
                signals: [
                    {
                        type: "stalled",
                        first: !state.stalled,
                        connection: state.connection,
                        stuckMs: event.at - (state.stallClockSince ?? event.at),
                    },
                ],
            }
        }

        case "SNAPSHOT_READ":
            return {
                state: { ...state, snapshot: event.snapshot ?? state.snapshot, snapshotFailures: 0 },
                signals: [],
            }

        case "SNAPSHOT_FAILED": {
            const exit = exitForStatus(event.status)
            if (exit != null && event.status != null) {
                if (state.exit != null) return { state, signals: [] }
                return {
                    state: { ...state, exit },
                    signals: [{ type: "exit", exit, status: event.status }],
                }
            }
            const failures = state.snapshotFailures + 1
            return {
                state: { ...state, snapshotFailures: failures },
                signals: [{ type: "snapshot_failed", status: event.status, offline: event.offline, failures }],
            }
        }

        case "OTHER_TAB":
            if (event.present === state.otherTab) return { state, signals: [] }
            return { state: { ...state, otherTab: event.present }, signals: [] }
    }
}

/** La séance du jour, du relevé (`data.today_occurrence`) ; `null` si le serveur ne la sert pas. */
export function snapshotOf(data: unknown): RoomSnapshot | null {
    const occurrence = (data as { today_occurrence?: unknown } | null)?.today_occurrence as
        { id?: unknown; finalized?: unknown; finalized_at?: unknown } | null | undefined
    if (occurrence == null || typeof occurrence !== "object") return null
    return {
        occurrenceId: typeof occurrence.id === "number" ? occurrence.id : null,
        finalized: occurrence.finalized === true,
        finalizedAt: typeof occurrence.finalized_at === "string" ? occurrence.finalized_at : null,
    }
}
